/**
 * Small preview thumbnails for queued files.
 *
 * The upload screens used to point `<img src>` straight at a blob URL of the
 * original file. A 5MB phone photo is around 12 megapixels, and the browser has to
 * decode all of it to paint a 160px card - roughly 48MB of RGBA per image, held for
 * as long as the card is mounted. Add 39 of them and the tab is juggling well over
 * a gigabyte of decoded bitmaps, which is what made simply *adding* files slow.
 *
 * A thumbnail is ~15KB and decodes in microseconds. Generating it costs one decode
 * per file, and `createImageBitmap`'s `resizeWidth` lets the browser decode
 * straight to the target size on its own thread, so a full-resolution bitmap never
 * exists.
 */

const DEFAULT_MAX_DIMENSION = 320;
const THUMBNAIL_QUALITY = 0.7;

/**
 * Concurrency cap. Decoding is off-thread, but each in-flight decode still holds
 * memory, and 39 at once defeats the point of not holding full-size bitmaps.
 */
const MAX_CONCURRENT = 4;

let activeCount = 0;
const waiting: Array<() => void> = [];

const acquireSlot = (): Promise<void> =>
  new Promise((resolve) => {
    if (activeCount < MAX_CONCURRENT) {
      activeCount += 1;
      resolve();
      return;
    }
    waiting.push(() => {
      activeCount += 1;
      resolve();
    });
  });

const releaseSlot = () => {
  activeCount -= 1;
  waiting.shift()?.();
};

/**
 * Results are cached per `File`, so a component re-mounting (or StrictMode's
 * double-invoke) does not pay for the decode twice. A `WeakMap` means the entry
 * disappears with the file.
 */
const thumbnailCache = new WeakMap<File, Blob>();

/**
 * Renders `file` to a small JPEG. Returns null for anything that is not a
 * decodable image, or when the browser cannot do it - callers fall back to a
 * placeholder rather than to the full-size original.
 */
export const createThumbnailBlob = async (
  file: File,
  maxDimension: number = DEFAULT_MAX_DIMENSION,
): Promise<Blob | null> => {
  if (!file.type.startsWith("image/")) return null;

  const cached = thumbnailCache.get(file);
  if (cached) return cached;

  if (typeof createImageBitmap !== "function") return null;

  await acquireSlot();

  let bitmap: ImageBitmap | null = null;
  try {
    // `imageOrientation: "from-image"` lets the browser apply EXIF rotation. Unlike
    // the upload path this is display-only, so there is no metadata to preserve and
    // no reason to redo the rotation by hand.
    try {
      bitmap = await createImageBitmap(file, {
        imageOrientation: "from-image",
        resizeWidth: maxDimension,
        resizeQuality: "medium",
      });
    } catch {
      // Older Safari ignores or rejects the resize options; decode fully and scale
      // on the way into the canvas instead.
      bitmap = await createImageBitmap(file, {
        imageOrientation: "from-image",
      });
    }

    const scale = Math.min(
      1,
      maxDimension / Math.max(bitmap.width, bitmap.height),
    );
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", THUMBNAIL_QUALITY);
    });

    if (blob) thumbnailCache.set(file, blob);
    return blob;
  } catch {
    // A preview is cosmetic; never let it break adding files.
    return null;
  } finally {
    bitmap?.close?.();
    releaseSlot();
  }
};
